import { useRef, useState } from 'react';
import { useComboDismiss } from '@hooks/useComboDismiss';
import { ChevronDownIcon } from '@utils/icons';
import { fold } from '@utils/text';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './PickCombo.module.css';
import { isImeComposing } from '@utils/ime';

export interface IPickComboOption {
  /** Stable identity handed back on pick. */
  value: string;
  /** What the row (and the trigger, once picked) renders. */
  label: string;
  /** Faded second line under the label — what tells two same-named rows apart. */
  hint?: string;
  /** Draws a thin divider above this row (a different kind of choice). */
  separatorBefore?: boolean;
  /** Picking this row calls `onPick` but leaves the list OPEN — for a row
   *  that swaps the list for another one (an "everything else…" expander)
   *  rather than settling the value. */
  keepsOpen?: boolean;
  /** Survives a typed filter that matches nothing in it — an escape row that
   *  must stay reachable however the user has narrowed the list. */
  pinned?: boolean;
}

export interface IPickComboProps {
  value: string;
  options: IPickComboOption[];
  /** Shown faded whenever `value === ''` — even when an option with value ''
   *  exists (a "clear" row), so an unset field never reads as a pick. */
  placeholder: string;
  onPick: (value: string) => void;
  /** Lands on the trigger — pair it with a `<label htmlFor>`. */
  id?: string;
  /** `data-testid` on the trigger. */
  testId?: string;
  /** Extra trigger class — a consumer's own marking (the delivery form's
   *  amber "we seeded this" colour). */
  className?: string;
  ariaLabelledBy?: string;
  ariaInvalid?: boolean;
  /** The list opens upward — for a form's bottom-most field. */
  dropUp?: boolean;
  /** Locked: the trigger is faded and inert and the list never opens — for a
   *  field that only makes sense once another is filled. The placeholder
   *  says why. */
  disabled?: boolean;
  /** The trigger is an <input>: typing narrows the list (case- and
   *  width-insensitive, anywhere in the label or hint) and Enter picks the
   *  first match. Still pick-only — the typed text is a filter, never a
   *  value — so a long list (hundreds of CAD materials) is reachable by
   *  keyboard. Off, the trigger is a <button>. */
  filterable?: boolean;
  /** Mirrors the typed filter: the text on each keystroke, `null` once it is
   *  dropped (pick, close). Read synchronously inside `onPick`, it is the text
   *  the pick was made over — what an escape row ("+ New…") prefills from. */
  onQueryChange?: (query: string | null) => void;
}

/**
 * The pick-only dropdown: a trigger wearing the field skin, a caret, and a
 * `role="listbox"` of <button> options that closes on pick, on a pointerdown
 * outside (`useComboDismiss`) and on Tab-away. Never free text — even
 * `filterable`, what the user types only narrows the rows; the value is
 * always one of them.
 *
 * A `value` that matches no option and is not empty renders as-is: a stored
 * unit from before the vocabulary changed still shows what the record holds.
 */
export function PickCombo({
  value,
  options,
  placeholder,
  onPick,
  id,
  testId,
  className,
  ariaLabelledBy,
  ariaInvalid,
  dropUp,
  disabled,
  filterable,
  onQueryChange,
}: IPickComboProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const combo = useComboDismiss(ref);
  // The typed filter, alive only while the list is open; `null` = not typing,
  // the trigger shows the picked value.
  const [query, setRawQuery] = useState<string | null>(null);
  const setQuery = (next: string | null) => {
    setRawQuery(next);
    onQueryChange?.(next);
  };
  const empty = value === '';
  const match = options.find((o) => o.value === value);
  const text = empty ? placeholder : match ? match.label : value;
  const triggerCls = [styles.trigger, empty && styles.placeholder, className].filter(Boolean).join(' ');

  const needle = query === null ? '' : fold(query.trim());
  const shown =
    needle === ''
      ? options
      : options.filter((o) => o.pinned || fold(`${o.label} ${o.hint ?? ''}`).includes(needle));

  function close() {
    combo.setOpen(false);
    setQuery(null);
  }

  function pick(o: IPickComboOption) {
    onPick(o.value);
    if (o.keepsOpen) setQuery(null);
    else close();
  }

  const trigger = filterable ? (
    <input
      id={id}
      data-testid={testId}
      type="text"
      className={triggerCls}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={combo.open}
      aria-autocomplete="list"
      aria-labelledby={ariaLabelledBy}
      aria-invalid={ariaInvalid || undefined}
      disabled={disabled}
      placeholder={placeholder}
      autoComplete="off"
      // While typing the box holds the filter; otherwise the picked label
      // (the placeholder attribute covers the empty case).
      value={query ?? (empty ? '' : text)}
      onClick={combo.toggle}
      onChange={(e) => {
        setQuery(e.target.value);
        combo.setOpen(true);
      }}
      onKeyDown={(e) => {
        if (isImeComposing(e)) return;
        if (e.key === 'Enter' && combo.open) {
          e.preventDefault();
          // The first row that is a real choice, not a pinned escape hatch —
          // unless the filter left nothing else.
          const first = shown.find((o) => !o.pinned) ?? shown[0];
          if (first) pick(first);
        } else if (e.key === 'Escape') {
          close();
        }
      }}
    />
  ) : (
    <button
      id={id}
      data-testid={testId}
      type="button"
      className={triggerCls}
      aria-haspopup="listbox"
      aria-expanded={combo.open}
      aria-labelledby={ariaLabelledBy}
      aria-invalid={ariaInvalid || undefined}
      disabled={disabled}
      onClick={combo.toggle}
    >
      {text}
    </button>
  );

  return (
    <div
      ref={ref}
      className={styles.combo}
      onBlur={(e) => {
        // Keyboard path (Tab away); pointer paths are handled by the hook's
        // document pointerdown listener. Either way a half-typed filter is
        // discarded — the field goes back to showing its value.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close();
      }}
    >
      {trigger}
      <button
        type="button"
        className={styles.caretBtn}
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
        onClick={combo.toggle}
      >
        <ChevronDownIcon size={12} />
      </button>
      {combo.open ? (
        <div
          className={dropUp ? `${styles.list} ${styles.listUp}` : styles.list}
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
        >
          <ScrollArea>
            {shown.map((o) => (
              <button
                key={o.value}
                type="button"
                className={o.separatorBefore ? `${styles.opt} ${styles.optSeparator}` : styles.opt}
                onClick={() => pick(o)}
              >
                {o.label}
                {o.hint ? <span className={styles.optHint}>{o.hint}</span> : null}
              </button>
            ))}
          </ScrollArea>
        </div>
      ) : null}
    </div>
  );
}
